/// <amd-module name="typeahead/features/CurrentLocationSuggester" />
/// <reference path="../../typings/typeahead/IPageLoadHandler.d.ts"/>
import {ClientHandlerRegistry} from "typeahead/client/ClientHandlerRegistry";
import {Experiment} from "typeahead/core/Experiment";
import {GeolocationDataProvider} from "typeahead/core/geolocation/GeolocationDataProvider";
import {GeolocationViewController} from "typeahead/core/geolocation/GeolocationViewController";
import {GeolocationMetricLogger} from "typeahead/core/geolocation/logging/GeolocationMetricLogger";
import {IDataProviderResponse} from "typeahead/core/IDataProvider";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {IFocusSuggester} from "typeahead/core/IFocusSuggester";
import {ISuggesterResponse} from "typeahead/core/ISuggester";
import {FormLoadedMessage} from "typeahead/core/message/FormLoadedMessage";
import {SuggestionAutoSelectedMessage} from "typeahead/core/message/SuggestionAutoSelectedMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {prefetchInstance} from "typeahead/core/plugins/CompositePageLoadHandler";
import * as ExecutionHelper from "typeahead/core/plugins/ExecutionHelper";
import * as SearchParametersUpdater from "typeahead/core/SearchParametersUpdater";
import * as StripingConfiguration from "typeahead/core/StripingConfiguration";
import {IStripingConfiguration} from "typeahead/core/StripingConfiguration";
import {IEssSuggestionResult} from "typeahead/core/Suggestion";
import * as SuggestionFactory from "typeahead/core/SuggestionFactory";
import {SuggesterId} from "typeahead/core/SuggestionsAggregator";
import {Lob, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

const configuration: IStripingConfiguration[] = [{
        deviceTypes: ["Tablet", "Mobile"],
        tabs: ["hotel"],
    },
    {
        clients: ["Homepage", "AWSHomepage"],
        deviceTypes: ["Desktop"],
        tabs: ["activity"],
    },
    {
        clients: ["LXLandingPage"],
        deviceTypes: ["Desktop"],
    },
];

const nearbyCurrentLocationConfiguration: IStripingConfiguration[] = [
    {
        clients: ["Homepage", "LXLandingPage"],
        deviceTypes: ["Tablet", "Mobile"],
        experiment: {
            id: 26592,
            name: "ess_nearby_instead_of_current_location_in_LX",
            value: 1,
        },
    },
];

const allConfigs = [configuration, nearbyCurrentLocationConfiguration];

const applicableClients: string[] = [
                                        "Homepage",
                                        "AWSHomepage",
                                        "HotelsLandingPage",
                                        "FlightsLandingPage",
                                        "PackageLandingPage",
                                        "LXLandingPage",
                                    ];

export class CurrentLocationSuggester implements IPageLoadHandler, IFocusSuggester, IExperimentalFeature {

    // When showing popular destinations, there can be 2 steps of typeahead suggestions
    private static isFirstStepInMultiStepTypeahead(): boolean {
        return window.location.search.indexOf("&depth=") < 0;
    }

    private geolocationDataProvider: GeolocationDataProvider;
    private geolocationViewController: GeolocationViewController;
    private geolocationMetricLogger: GeolocationMetricLogger;

    public handlePageLoad(options: any, context: IContext): void {
        if ( applicableClients.indexOf(context.clientId) > -1) {
            TypeaheadMessage.subscribe(FormLoadedMessage.getTopic(), (topic: string, message: FormLoadedMessage) => {
                ExecutionHelper.abTest.getBucketingPromise().done(() => {
                    context.tab = message.getTab();
                    if (TypeaheadUtils.isWithinMobileBreakpoint()) {
                        if (TypeaheadUtils.getLob() === Lob.Activity
                            && ExecutionHelper.shouldExecute(context, nearbyCurrentLocationConfiguration)) {
                            this.autoPopulateNearbyCurrentLocation(context.lob);
                        } else if (ExecutionHelper.shouldExecute(context, [].concat(configuration))) {
                            this.initialize();
                            if (this.geolocationViewController) {
                                this.geolocationViewControllerPageloadHandler(context);
                            }
                        }
                    }
                });
            });
        }
    }

    // whenever showOnFocusSuggestions() is triggered from core uta.js
    public suggestOnFocus(options: any, context: IContext): JQueryPromise<ISuggesterResponse> {
        return this.suggest(options, context);
    }

    // Current location suggest is now called whenever onfocus suggestions are enabled
    public suggest(options: any, context: IContext): JQueryPromise<ISuggesterResponse> {
        const deferred: JQueryDeferred<ISuggesterResponse> = $.Deferred();
        const emptySuggesterResponse: ISuggesterResponse = {id: this.getId(), requestId: "", results: []};
        if (ExecutionHelper.shouldExecute(context, configuration)
            && CurrentLocationSuggester.isFirstStepInMultiStepTypeahead()) {
            this.initialize();
            this.geolocationDataProvider.fetchData(options).done((response: IDataProviderResponse) => {
                deferred.resolve({id: this.getId(), requestId: "", results: response.results});
            }).fail(() => {
                deferred.resolve(emptySuggesterResponse);
            });
        } else {
            deferred.resolve(emptySuggesterResponse);
        }
        return deferred.promise();
    }

    public getId(): SuggesterId {
        return SuggesterId.CurrentLocationSuggester;
    }

    public getExperiments(): Experiment[] {
        return StripingConfiguration.getExperiments([].concat.apply([], allConfigs));
    }

    private autoPopulateNearbyCurrentLocation(lob: string) {
        const $inputElement = $("form[data-lob-form=activity]").find("input[name=location]");
        const elemId = $inputElement.attr("id");

        prefetchInstance.getNearbyLocationLXSuggestion()
            .done((nearbyCurrentLocationSuggestion: IEssSuggestionResult) => {
                if (nearbyCurrentLocationSuggestion) {
                    const suggestion = SuggestionFactory.createSuggestion(nearbyCurrentLocationSuggestion);

                    $inputElement.val(nearbyCurrentLocationSuggestion.regionNames.lastSearchName);
                    SearchParametersUpdater.updateForSuggestion(null, suggestion, $inputElement.data(), elemId);
                    TypeaheadMessage.publish(new SuggestionAutoSelectedMessage(suggestion, elemId));
                    TypeaheadMessage.publish(new SuggestionSelectedMessage(suggestion, "", null, lob, elemId));
                }
            });
    }

    private initialize(): void {
        if (!this.geolocationDataProvider) {
            this.geolocationDataProvider = new GeolocationDataProvider();
            this.geolocationViewController =
                ClientHandlerRegistry.getGeolocationViewController(this.geolocationDataProvider);
            this.geolocationMetricLogger = new GeolocationMetricLogger();
        }
    }

    private geolocationViewControllerPageloadHandler(context: IContext) {
        this.handlePageLoadPrefetchHelper(context);
    }

    private mustPopulateCurrentLocationConfigHelper(choice: boolean, context: IContext,
                                                    config: IStripingConfiguration[]): void {
        if ((config && ExecutionHelper.shouldExecute(context, config)) || config === null) {
            this.geolocationViewController.handlePageLoad(context, choice);
        }
    }

    private handlePageLoadPrefetchHelper(context: IContext): void {
        if (!TypeaheadUtils.isWithinMobileBreakpoint()) {
            // Default current location behavior request
            this.mustPopulateCurrentLocationConfigHelper(false, context, null);
        }
    }
}
